import { SharedCardData } from '../pages/SharedCards/types';
import api from './api/api';

export interface InsuredWallet {
  userUnimedId: string;
  insuredName: string;
  cardNumber: string;
  planName: string;
  cardExpirateDate: string;
}

export interface CardSharingItem {
  name: string | null;
  cardNumber: string | null;
  cpf: string;
  sharingType: string;
  startDate: string;
  token: string;
}

export interface ProcessedCardSharingItem {
  sharingId: string;
  token: string;
  insuredName: string;
  sharingDate: string;
  status: string;
}

export interface ExternalAccessWallet {
  cardNumber: string;
  insuredName: string;
  planName: string;
  cardExpirateDate: string;
  relationship: string;
}

export interface CreateSharingResponse {
  accessToken: string;
}

export interface ApiResponse<T> {
  result: number;
  message: string;
  data: T;
}

export interface GenericDataArray<T> {
  content?: T[];
  message: string;
}

export interface GenericData<T> {
  content?: T;
  message: string;
}

export interface UnimedUserInfo {
  sharedUserUnimedId?: number;
  cardNumberShared?: string;
  access: string;
}

export interface CtaInfo {
  title: string;
  message: string;
}

class CardSharingService {
  private getHeaders = () => ({
    'Content-Type': 'application/json',
  });

  async getInsuredWallets(userUnimedId: string): Promise<InsuredWallet[]> {
    try {
      const response = await api.get<ApiResponse<InsuredWallet[]>>(
        `/card-sharing/insured-wallets?userUnimedId=${userUnimedId}`,
        {
          headers: this.getHeaders(),
        },
      );

      return response.data?.data;
    } catch (error) {
      console.error('Erro ao buscar carteiras do segurado:', error);
      throw error;
    }
  }

  async getCardSharingList(userPlanCode: string): Promise<CardSharingItem[]> {
    try {
      const response = await api.get<GenericDataArray<CardSharingItem>>(
        `/card-sharing/list?userPlanCode=${userPlanCode}`,
        {
          headers: this.getHeaders(),
        },
      );

      return response.data?.content || [];
    } catch (error) {
      console.error('Erro ao buscar lista de compartilhamentos:', error);
      throw error;
    }
  }

  async getExternalAccessWallets(
    cpf: string,
    token: string,
  ): Promise<SharedCardData[] | null> {
    try {
      const response = await api.post<SharedCardData[]>(
        `/card-sharing/external-access-wallets?cpf=${cpf}&token=${token}`,
        {},
        {
          headers: this.getHeaders(),
        },
      );

      return response.data ?? null;
    } catch (error) {
      console.error('Erro ao buscar carteiras para acesso externo:', error);
      throw error;
    }
  }

  async generateInsuredSharing(
    userUnimedId: string,
    sharedUserUnimedId: string,
  ): Promise<CreateSharingResponse> {
    try {
      const response = await api.post<ApiResponse<CreateSharingResponse>>(
        `/card-sharing/generate-insured`,
        {
          userUnimedId,
          sharedUserUnimedId,
        },
        {
          headers: this.getHeaders(),
        },
      );

      return response.data?.data;
    } catch (error) {
      console.error('Erro ao gerar compartilhamento entre segurados:', error);
      throw error;
    }
  }

  async generateExternalSharing(
    cpf: string,
    name: string,
    sharedUserUnimedId: string,
  ): Promise<GenericDataArray<CreateSharingResponse>> {
    try {
      const response = await api.post<GenericDataArray<CreateSharingResponse>>(
        `/card-sharing/generate-external`,
        {
          cpf,
          name,
          sharedUserUnimedId,
        },
        {
          headers: this.getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('Erro ao gerar compartilhamento externo:', error);
      throw error;
    }
  }

  async deleteSharing(token: string): Promise<void> {
    try {
      await api.delete('/card-sharing/delete-share', {
        headers: this.getHeaders(),
        params: {
          token,
        },
      });
    } catch (error) {
      console.error('Erro ao excluir compartilhamento:', error);
      throw error;
    }
  }

  async getUnimedUserInfoFromCpf(
    cpf: string,
  ): Promise<GenericDataArray<UnimedUserInfo>> {
    try {
      const response = await api.get<GenericDataArray<UnimedUserInfo>>(
        `/card-sharing/get-unimed-user-info-from-cpf`,
        {
          headers: this.getHeaders(),
          params: {
            cpf,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Erro ao buscar informações do usuário Unimed:', error);
      throw error;
    }
  }

  async getCtaInfo(): Promise<GenericData<CtaInfo> | null> {
    try {
      const response = await api.get<ApiResponse<CtaInfo>>(
        '/card-sharing/cta-config',
        {
          headers: this.getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return null;
    }
  }
}

export const cardSharingService = new CardSharingService();
export default cardSharingService;
